import './global-setup';

import { createReactiveState } from '@/utils/reactive-store';

// import type { ConfigurationSetup } from './config/config-setup.types';
import { DynamicConfig } from './DynamicConfig';
import { TeoError } from './errors/TeoError';
import { TeoApi, type TeoApiConfig, type UsageQuota } from './TeoApi';
import { TeoHelp } from './TeoHelp';
import { TeoNotifications } from './TeoNotifications';
import { TeoStorage } from './TeoStorage';
import { TeoText, type AnalysisConfiguration } from './text/TeoText';
import { TextCreator } from './TextCreator';
import { User } from './User';
import { createCopyName } from './utility/miscFunctions';
import { TeoEventTarget } from './utility/TeoEventTarget';

export interface TeoCoreConfig extends TeoApiConfig {
  elements: string[];
  evaluationProfile: number;
  tpiTarget: number;
}

type CoreEvents = {
  activeTextChanged: (detail: TeoText) => void;
  textsChanged: (detail: TeoText[]) => void;
  coreChanged: (detail: TeoCore) => void;
};

export interface TeoCoreState {
  activeTextIndex: number | null;
  activeText: TeoText | null;
  analysisUsageQuota: {
    total: number;
    used: number;
  };
}

export class TeoCore {
  static #instance: TeoCore | null = null;

  static get instance() {
    if (!TeoCore.#instance) {
      throw new Error('TeoCore not initialized');
    }
    return TeoCore.#instance;
  }

  private static set instance(teoCore: TeoCore) {
    if (TeoCore.#instance && TeoCore.#instance !== teoCore) {
      if (process.env.NODE_ENV === 'development') {
        // dispatch an event on the old core
        TeoCore.#instance.events.triggerEvent('coreChanged', teoCore);
        // TODO: maybe cleanup old core?
      } else {
        throw new Error('TeoCore already initialized');
      }
    }
    TeoCore.#instance = teoCore;
  }

  api: TeoApi;
  staticConfig: TeoCoreConfig;
  dynamicConfig: DynamicConfig;
  events = new TeoEventTarget<CoreEvents>();

  user: User;

  texts: TeoText[] = [];

  help: TeoHelp;
  notifications: TeoNotifications = new TeoNotifications();
  textCreator: TextCreator;
  private activeTextIndex: number | null = null;

  public state = createReactiveState<TeoCoreState>({
    activeTextIndex: null,
    activeText: null,
    analysisUsageQuota: {
      total: -1,
      used: -1,
    },
  });

  defaultConfigValues = {
    brandFitEnabled: true,
    tpiTarget: 90,
  };

  private textCounter = 0;

  storage: TeoStorage = new TeoStorage();

  constructor(config: TeoCoreConfig) {
    TeoCore.#instance = this;
    this.staticConfig = config;
    this.staticConfig = { ...this.defaultConfigValues, ...this.staticConfig };
    this.dynamicConfig = new DynamicConfig({
      evaluationProfileId: this.staticConfig.evaluationProfile ?? 3,
    });
    this.api = new TeoApi(this, {
      apiBaseUrl: config.apiBaseUrl,
      useInsecureHttp: config.useInsecureHttp,
    });
    this.user = new User(this);
    this.help = new TeoHelp(this);
    this.textCreator = new TextCreator(this);

    this.user.events.addEventListener('logOut', (reset) => {
      if (reset) this.reset();
    });

    this.user.events.addEventListener('logIn', this.updateConfigOptions.bind(this));
    this.dynamicConfig.addEventListener('evaluationProfileId', this.updateConfigOptions.bind(this));
    this.dynamicConfig.addEventListener('subscriptionName', this.updateAppTitle);
  }

  static async init(config: TeoCoreConfig) {
    applyWebComponentGetElementByIdPatch(config.elements);
    const teoCore = new TeoCore(config);
    await teoCore.initTexts();
    if (teoCore.staticConfig.elements) {
      teoCore.registerElements(...teoCore.staticConfig.elements);
    }
    return teoCore;
  }

  get activeText(): TeoText {
    if (this.activeTextIndex === null || !this.texts[this.activeTextIndex]) {
      if (this.texts.length > 0) {
        this.activeTextIndex = 0;
      } else {
        this.texts.push(new TeoText(this, this.textCounter++));
        this.activeTextIndex = 0;
      }
      this.events.triggerEvent('activeTextChanged', this.texts[this.activeTextIndex]!);
    }
    return this.texts[this.activeTextIndex]!;
  }

  async initTexts() {
    const texts = await this.storage.loadTexts();
    if (texts) {
      let maxId = 0;
      this.texts = texts.map((textData) => {
        const text = TeoText.make({
          core: this,
          ...textData,
          uiState: { ...textData.uiState, title: textData.title },
          options: { shouldStoreAfterCreate: false },
        });
        if (textData.analysis) {
          // text.analysis = AnalysisResult.fromJSON(text, textData.analysis);
          // ???: do we need to trigger an event here?
        }
        if (textData.history) {
          text.history.restoreFromJSON(textData.history);
          // ???: do we need to trigger an event here?
        }
        if (textData.id > maxId) {
          maxId = textData.id;
        }
        return text;
      });
      this.textCounter = maxId + 1;
    }
    if (this.texts.length === 0) {
      this.texts.push(new TeoText(this, this.textCounter++));
      this.activeTextIndex = 0;
      this.events.triggerEvent('activeTextChanged', this.texts[this.activeTextIndex]!);
    }
  }

  addText(
    textToAdd: string | TeoText = '',
    setActive = true,
    configuration: AnalysisConfiguration | undefined = undefined,
  ) {
    let text: TeoText;
    if (typeof textToAdd === 'string') {
      text = new TeoText(this, this.textCounter++, textToAdd, undefined, configuration);
    } else {
      text = textToAdd;
      text.id = this.textCounter++;
      text.ui.state.view = 'performance';
    }
    this.texts.push(text);
    if (setActive) {
      this.activeTextIndex = this.texts.length - 1;
      this.events.triggerEvent('activeTextChanged', text);
    }
    this.storage.saveText(text.getTextData());
    this.events.triggerEvent('textsChanged', this.texts);
    return text;
  }

  switchText(id: number) {
    const index = this.texts.findIndex((text) => text.id === id);
    if (index !== -1) {
      this.activeTextIndex = index;
      this.events.triggerEvent('activeTextChanged', this.texts[this.activeTextIndex]!);
    }
  }

  removeText(id: number) {
    const index = this.texts.findIndex((text) => text.id === id);

    if (index !== -1) {
      if (
        this.activeTextIndex !== null &&
        this.texts[this.activeTextIndex] &&
        this.texts[this.activeTextIndex]!.id === id &&
        this.texts[0]
      )
        this.switchText(this.texts[0].id);

      this.texts[index]?.dispose();
      this.texts.splice(index, 1);
      this.storage.deleteText(id);
      this.events.triggerEvent('textsChanged', this.texts);
    }
    if (this.texts.length === 0) {
      this.addText();
    }
  }

  duplicateText(id: number) {
    const text = this.texts.find((text) => text.id === id);
    const textNames = this.texts.map((text) => text.ui.state.title);
    if (text) {
      const newText = new TeoText(
        this,
        this.textCounter++,
        text.text,
        text.serialized,
        text.configuration,
        undefined,
        { title: createCopyName(text.ui.state.title, textNames), view: text.ui.state.view },
        undefined,
        text.generatorUi.toJSON(),
      );

      this.texts.push(newText);
      this.storage.saveText(newText.getTextData());
      this.events.triggerEvent('textsChanged', this.texts);
      return newText;
    }
  }

  createTemporaryText(
    textString: string = '',
    configuration: AnalysisConfiguration | undefined = undefined,
  ) {
    const text = new TeoText(
      this,
      this.textCounter++,
      textString,
      undefined,
      configuration,
      undefined,
      undefined,
      { isTemporary: true }, // ???: should it be stored?
    );
    return text;
  }

  getText(id: number) {
    return this.texts.find((text) => text.id === id);
  }

  getLogInStatus() {
    return this.api.apiToken !== '';
  }

  async getTextConfigOptions() {
    if (!this.dynamicConfig.options.textConfigOptions) {
      await this.updateConfigOptions();
    }
    const textConfigOptions = this.dynamicConfig.options.textConfigOptions;
    if (!textConfigOptions) {
      throw new Error('Missing text config options');
    }
    return textConfigOptions;
  }

  async updateConfigOptions(): Promise<void> {
    if (!this.dynamicConfig.options.evaluationProfileId) {
      throw new Error('No evaluation profile set in TeoCore configuration.');
    } else {
      const result = await this.api.getDynamicConfigurationSetup(
        this.dynamicConfig.options.evaluationProfileId,
      );
      if (TeoError.isError(result)) {
        throw new Error(`API error: ${result.message}`);
      }

      const { sections, usageQuotas, ...flags } = result;

      const textConfigOptions =
        sections ?
          {
            ...this.dynamicConfig.options.textConfigOptions,
            sections,
          }
        : this.dynamicConfig.options.textConfigOptions;

      this.dynamicConfig.updateOptions({
        ...flags,
        textConfigOptions,
      });

      usageQuotas?.forEach((quota) => this.updateUsageQuota(quota));
    }
  }

  registerElements(...elementNames: string[]) {
    elementNames.forEach((elementName) => {
      const element = document.querySelector(elementName);
      if (element) {
        (element as TeoElement).teoCore = this;
      }
    });
    return this;
  }

  reset() {
    this.texts.forEach((text) => text.dispose());
    this.storage.clearEverything();
    this.texts = [];
    this.textCounter = 0;
    this.activeTextIndex = null;
    this.events.triggerEvent('activeTextChanged', this.activeText);
    this.events.triggerEvent('textsChanged', this.texts);
  }

  static handleError(error: unknown) {
    // TODO: add some logic, for now just notify
    TeoCore.instance.notifications.addError(error);
  }

  updateAppTitle(subscriptionName: string) {
    document.title = subscriptionName ? `TEO - ${subscriptionName}` : 'TEO';
  }

  updateUsageQuota(usageQuota: UsageQuota) {
    if (usageQuota.type === 'ANALYSIS') {
      if (usageQuota.usage_quota) {
        this.state.analysisUsageQuota.total = usageQuota.usage_quota;
        this.state.analysisUsageQuota.used = usageQuota.used_quota;
      }
    }
  }
}

interface TeoElement extends Element {
  teoCore: TeoCore;
}

// patch for melt
const webComponentsMap = new Map<string, Element>();
const prevGetElementById = document.getElementById;
function applyWebComponentGetElementByIdPatch(elementNames: string[]) {
  document.getElementById = (id: string) => {
    const el = prevGetElementById.call(document, id);
    if (el) return el; // found in document

    // search in webComponents...
    for (const elementName of elementNames) {
      let wc = webComponentsMap.get(elementName);
      if (!wc) {
        wc = document.getElementsByTagName(elementName)[0];
        if (wc) {
          webComponentsMap.set(elementName, wc);
        }
      }
      if (wc) {
        const elem = wc.shadowRoot?.getElementById(id);
        if (elem) {
          return elem;
        }
      }
    }
    return null;
  };
}
